import { useState, useEffect } from "react";
import {
  runElaborazioneGiorno,
  getElaborazioneGiorno,
  listElaborazioni,
  deleteElaborazioneGiorno,
  deleteAllElaborazioni,
  runElaborazioneCassa,
  getElaborazioneCassaGiorno,
  getAnniDisponibili,
  getElaborazioneGiornoMovimenti,
  runElaborazionePeriodo,
  loadElaborazionePeriodo,
  listPeriodiAggregati,
  getPdcPivot,
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const fmtEuro = (v) => (Number(v) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayDmy = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const dmyToIso = (dmy) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}` : "";
};
const isoToDmy = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const fmtUpdated = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function ElaborazioneGiornoPage({ forcedMode = null }) {
  const { annoCorrente } = useAuth();
  const [mode, setMode] = useState(forcedMode || "giorno"); // "giorno" | "periodo" | "cassa"
  // Default date range: per periodo/cassa parte dal 01 del mese corrente ad oggi
  const _defaultRangeStart = () => {
    const now = new Date();
    return `01/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
  };
  const [data, setData] = useState(forcedMode === "periodo" || forcedMode === "cassa" ? _defaultRangeStart() : todayDmy());
  const [dataA, setDataA] = useState(todayDmy());
  const [anno, setAnno] = useState(annoCorrente);
  const [anniDisponibili, setAnniDisponibili] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [periodoResult, setPeriodoResult] = useState(null);
  const [cassaResult, setCassaResult] = useState(null);
  const [cassaOverrides, setCassaOverrides] = useState({}); // {dd/mm/yyyy: dd/mm/yyyy}
  const [storico, setStorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mostraZero, setMostraZero] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [storicoFilter, setStoricoFilter] = useState(forcedMode === "cassa" ? "cassa" : forcedMode === "periodo" ? "pdc" : "all"); // "all" | "pdc" | "cassa"

  // Vista 1 (lista periodi) solo per forcedMode="periodo"
  const [periodiList, setPeriodiList] = useState(null); // null = non caricato, [] = vuoto, [...] = lista
  const [periodiLoading, setPeriodiLoading] = useState(false);
  // Contesto del periodo "aperto" che permette di tornare alla Vista 2 (giorni del periodo)
  const [periodoContext, setPeriodoContext] = useState(null); // {data_da, data_a, anno}

  const caricaPeriodi = async () => {
    setPeriodiLoading(true);
    try {
      const r = await listPeriodiAggregati();
      setPeriodiList(r.data?.data || []);
    } catch (e) {
      console.error(e);
      setPeriodiList([]);
    } finally { setPeriodiLoading(false); }
  };

  const apriPeriodo = async (p) => {
    setLoading(true);
    try {
      const r = await loadElaborazionePeriodo(p.data_da, p.data_a, p.anno);
      setPeriodoResult(r.data);
      setData(p.data_da); setDataA(p.data_a); setAnno(p.anno);
      setSnapshot(null); setCassaResult(null);
      setPeriodoContext({ data_da: p.data_da, data_a: p.data_a, anno: p.anno });
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore caricamento periodo");
    } finally { setLoading(false); }
  };

  const tornaAiPeriodi = () => {
    setPeriodoResult(null);
    setSnapshot(null);
    setCassaResult(null);
    setPeriodoContext(null);
    setMode("periodo");
    caricaPeriodi();
  };

  const tornaAiGiorniPeriodo = async () => {
    if (!periodoContext) return;
    setLoading(true);
    try {
      const r = await loadElaborazionePeriodo(periodoContext.data_da, periodoContext.data_a, periodoContext.anno);
      setPeriodoResult(r.data);
      setSnapshot(null); setCassaResult(null);
      setData(periodoContext.data_da); setDataA(periodoContext.data_a); setAnno(periodoContext.anno);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore caricamento periodo");
    } finally { setLoading(false); }
  };

  const apriDettaglio = async (conto) => {
    setDetailLoading(true);
    setDetail({ conto, loading: true });
    try {
      const res = await getElaborazioneGiornoMovimenti(snapshot.data, snapshot.anno, conto);
      setDetail(res.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore caricamento dettaglio");
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const caricaStorico = async () => {
    try {
      const res = await listElaborazioni();
      setStorico(res.data.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getAnniDisponibili();
        const anni = res.data.anni || [];
        setAnniDisponibili(anni);
        if (anni.length) {
          setAnno(anni.includes(annoCorrente) ? annoCorrente : anni[0]);
        }
      } catch (e) { console.error(e); }
      await caricaStorico();
      if (forcedMode === "periodo") {
        await caricaPeriodi();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annoCorrente]);

  // Try to load existing snapshot when (giorno mode) data/anno change
  useEffect(() => {
    if (mode !== "giorno") return;
    if (!data || !anno) return;
    (async () => {
      try {
        const res = await getElaborazioneGiorno(data, anno);
        setSnapshot(res.data);
      } catch {
        setSnapshot(null);
      }
    })();
  }, [data, anno, mode]);

  const elabora = async () => {
    if (!data) { alert("Seleziona la data iniziale"); return; }
    if ((mode === "periodo" || mode === "cassa") && !dataA) { alert("Seleziona la data di fine periodo"); return; }
    if (mode !== "cassa" && !anno) { alert("Seleziona anno di competenza"); return; }
    setLoading(true);
    try {
      if (mode === "periodo") {
        const res = await runElaborazionePeriodo(data, dataA, anno);
        setPeriodoResult(res.data);
        setSnapshot(null); setCassaResult(null);
        await caricaStorico();
      } else if (mode === "cassa") {
        const res = await runElaborazioneCassa(data, dataA, cassaOverrides);
        setCassaResult(res.data);
        setSnapshot(null); setPeriodoResult(null);
        await caricaStorico();
      } else {
        const res = await runElaborazioneGiorno(data, anno);
        setSnapshot(res.data);
        setPeriodoResult(null); setCassaResult(null);
        await caricaStorico();
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore elaborazione");
    } finally {
      setLoading(false);
    }
  };

  const cambiaBonificoSrc = (rowDate, newSrc) => {
    setCassaOverrides(prev => ({ ...prev, [rowDate]: newSrc }));
  };
  const resettaOverride = (rowDate) => {
    setCassaOverrides(prev => {
      const n = { ...prev }; delete n[rowDate]; return n;
    });
  };

  const eliminaSnapshot = async () => {
    if (!snapshot) return;
    if (!window.confirm(`Eliminare l'elaborazione del ${snapshot.data} anno ${snapshot.anno}?`)) return;
    try {
      await deleteElaborazioneGiorno(snapshot.data, snapshot.anno);
      setSnapshot(null);
      await caricaStorico();
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const caricaStoricoItem = async (item) => {
    if (item.tipo === "cassa") {
      if (forcedMode && forcedMode !== "cassa") return;
      try {
        const res = await getElaborazioneCassaGiorno(item.data);
        setCassaResult(res.data);
        setSnapshot(null); setPeriodoResult(null);
        setMode("cassa");
        setData(item.data); setDataA(item.data);
      } catch (e) {
        alert(e?.response?.data?.detail || "Errore caricamento cassa");
      }
      return;
    }
    if (forcedMode === "cassa") return;
    if (forcedMode === "periodo") {
      // in periodo mode we just pre-fill the date range for a single day snapshot
      setData(item.data); setDataA(item.data); setAnno(item.anno);
      setPeriodoResult(null); setCassaResult(null);
      return;
    }
    setMode("giorno");
    setData(item.data);
    setAnno(item.anno);
    setPeriodoResult(null);
    setCassaResult(null);
  };

  const apriGiornoDaPeriodo = async (day) => {
    setMode("giorno");
    setData(day);
    // anno remains the same; useEffect will reload snapshot
    setPeriodoResult(null);
  };

  const stampa = () => {
    window.print();
  };

  const rows = snapshot?.rows || [];
  const visibleRows = mostraZero
    ? rows
    : rows.filter((r) => (r.tipo || "").toUpperCase() === "T" || r.num_movimenti > 0 || r.totale !== 0);

  return (
    <div data-testid="elaborazione-giorno-page">
      <div className="no-print" style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: (forcedMode === "periodo" && snapshot) ? "6px 16px" : "10px 16px",
        background: "#fff", borderBottom: "1px solid #ccc", flexWrap: "wrap",
        position: "sticky", top: 0, zIndex: 5, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <h1 style={{ margin: 0, fontSize: (forcedMode === "periodo" && snapshot) ? 14 : 18, borderBottom: "2px solid #8e24aa", paddingBottom: 2 }}>{forcedMode === "periodo" ? "Elaborazione Periodo" : forcedMode === "cassa" ? "Elaborazione Cassa" : "Elaborazione"}</h1>

        <div style={{ display: forcedMode ? "none" : "flex", background: "#f0f0f0", border: "1px solid #ccc", borderRadius: 3, overflow: "hidden" }}>
          <button
            data-testid="mode-periodo"
            onClick={() => setMode("periodo")}
            style={{ padding: "5px 12px", border: "none", background: mode === "periodo" ? "#0066cc" : "transparent", color: mode === "periodo" ? "white" : "#333", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
          >Periodo</button>
          <button
            data-testid="mode-cassa"
            onClick={() => setMode("cassa")}
            style={{ padding: "5px 12px", border: "none", background: mode === "cassa" ? "#2e7d32" : "transparent", color: mode === "cassa" ? "white" : "#333", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
          >Cassa</button>
        </div>

        <div style={{ display: (forcedMode === "periodo" && snapshot) ? "none" : "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 11, color: "#666" }}>{(mode === "periodo" || mode === "cassa") ? "Dal" : "Data pagamento"}</label>
          <input
            data-testid="filter-data"
            type="date"
            value={dmyToIso(data)}
            onChange={(e) => setData(isoToDmy(e.target.value))}
            className="sw-filter-input"
          />
        </div>
        {(mode === "periodo" || mode === "cassa") && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#666" }}>Al</label>
            <input
              data-testid="filter-data-a"
              type="date"
              value={dmyToIso(dataA)}
              onChange={(e) => setDataA(isoToDmy(e.target.value))}
              className="sw-filter-input"
            />
          </div>
        )}
        {mode !== "cassa" && !(forcedMode === "periodo" && snapshot) && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#666" }}>Anno di competenza</label>
            <select
              data-testid="filter-anno"
              value={anno}
              onChange={(e) => setAnno(Number(e.target.value))}
              className="sw-filter-input"
              style={{ width: 100 }}
            >
              {anniDisponibili.length === 0 && <option value={anno}>{anno}</option>}
              {anniDisponibili.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        )}
        {!(forcedMode === "periodo" && snapshot) && (
          <button data-testid="btn-elabora" className="sw-filter-btn sw-filter-btn-primary" onClick={elabora} disabled={loading}>
            {loading ? "Elaborazione..." : mode === "periodo" ? "Elabora periodo" : mode === "cassa" ? "Genera Cassa" : (snapshot ? "Ri-elabora e Salva" : "Elabora e Salva")}
          </button>
        )}
        {forcedMode === "periodo" && (periodoResult || snapshot) && (
          <button data-testid="btn-back-periodi" className="sw-filter-btn" onClick={tornaAiPeriodi}
            style={{ background: "#0288d1", color: "white", border: "1px solid #0288d1", fontWeight: 700 }}>
            ← Lista periodi
          </button>
        )}
        {forcedMode === "periodo" && snapshot && periodoContext && (
          <button data-testid="btn-back-giorni-periodo" className="sw-filter-btn" onClick={tornaAiGiorniPeriodo}
            style={{ background: "#00897b", color: "white", border: "1px solid #00897b", fontWeight: 700 }}>
            ← Giorni del periodo
          </button>
        )}
        {snapshot && mode === "giorno" && (
          <button data-testid="btn-elimina" className="sw-filter-btn" onClick={eliminaSnapshot} style={{ color: "#cc0000" }}>Elimina snapshot</button>
        )}
        {(snapshot || periodoResult || cassaResult) && (
          <button data-testid="btn-stampa" className="sw-filter-btn" onClick={stampa} title="Stampa risultato">🖨 Stampa</button>
        )}
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, cursor: "pointer", marginLeft: "auto" }}>
          <input type="checkbox" checked={!mostraZero} onChange={(e) => setMostraZero(!e.target.checked)} />
          Nascondi righe a zero
        </label>
      </div>

      <div className="elab-grid" style={{ display: "grid", gridTemplateColumns: forcedMode === "periodo" ? "1fr" : "1fr 320px", gap: 16, padding: "0 16px" }}>
        {/* Main snapshot */}
        <div>
          {/* Print-only header */}
          <div className="print-only" style={{ display: "none", marginBottom: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>canossaHUB · Elaborazione{cassaResult ? " Cassa" : ""}</div>
            <div style={{ fontSize: 11 }}>
              {cassaResult
                ? `Cassa dal ${cassaResult.data_da} al ${cassaResult.data_a}`
                : periodoResult
                  ? `Riepilogo periodo dal ${periodoResult.data_da} al ${periodoResult.data_a}`
                  : `Giorno ${snapshot?.data || ""}`}
              {!cassaResult && ` · Anno competenza ${(periodoResult || snapshot)?.anno || ""}`}
            </div>
            <div style={{ fontSize: 10, color: "#666" }}>Stampato il {new Date().toLocaleString("it-IT")}</div>
          </div>

          {forcedMode === "periodo" && !periodoResult && !snapshot ? (
            <PeriodiList
              loading={periodiLoading}
              items={periodiList || []}
              onOpen={apriPeriodo}
              onRefresh={caricaPeriodi}
            />
          ) : periodoResult ? (
            <PeriodoSummary result={periodoResult} onOpenDay={apriGiornoDaPeriodo} onBack={forcedMode === "periodo" ? tornaAiPeriodi : null} />
          ) : cassaResult ? (
            <CassaTable
              result={cassaResult}
              overrides={cassaOverrides}
              onChangeSrc={cambiaBonificoSrc}
              onReset={resettaOverride}
              onReElabora={elabora}
            />
          ) : snapshot ? (
            <div className="no-print" style={{ padding: "8px 12px", background: "#e8f5e9", border: "1px solid #81c784", fontSize: 12, marginBottom: 8, borderRadius: 3 }}>
              Snapshot salvato: <b>{snapshot.data}</b> · Anno <b>{snapshot.anno}</b> · Totale generale: <b style={{ fontSize: 14 }}>€ {fmtEuro(snapshot.grand_total)}</b> · Ultimo aggiornamento: <b>{fmtUpdated(snapshot.updated_at)}</b>
            </div>
          ) : forcedMode === "periodo" ? null : (
            <div className="no-print" style={{ padding: "8px 12px", background: "#fff8e1", border: "1px solid #ffd54f", fontSize: 12, marginBottom: 8, borderRadius: 3 }}>
              {mode === "periodo"
                ? "Seleziona il periodo e clicca 'Elabora periodo'. Verrà creato/aggiornato uno snapshot per ciascun giorno del range."
                : "Nessuna elaborazione salvata per questa combinazione. Clicca \"Elabora e Salva\" per creare lo snapshot."}
            </div>
          )}

          <div className="sw-table-container" style={{ display: (periodoResult || cassaResult || (forcedMode === "periodo" && !snapshot)) ? "none" : undefined }}>
            <div style={{ maxHeight: "70vh", overflow: "auto" }}>
              <table className="sw-table" style={{ fontSize: 11 }}>
                <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 70 }}>Conto</th>
                    <th>Descrizione</th>
                    <th style={{ width: 120 }}>Modo</th>
                    <th style={{ width: 80 }}>Anno</th>
                    <th style={{ width: 120 }}>Plesso</th>
                    <th style={{ width: 130 }}>Modalità</th>
                    <th style={{ width: 110 }}>Causale 1</th>
                    <th style={{ width: 110 }}>Causale 2</th>
                    <th style={{ width: 110 }}>Causale 3</th>
                    <th style={{ width: 55 }}>Cella</th>
                    <th style={{ width: 45 }}>Tipo</th>
                    <th style={{ width: 60, textAlign: "center" }}>Non St.</th>
                    <th style={{ width: 80, textAlign: "right", background: "#fff176", color: "#000" }}>N. Mov.</th>
                    <th style={{ width: 140, textAlign: "right", background: "#ffd54f", color: "#000", fontSize: 12 }}>TOTALE €</th>
                    <th className="no-print" style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {!snapshot ? (
                    <tr><td colSpan={15} className="sw-empty"><p>Seleziona data e anno, poi elabora</p></td></tr>
                  ) : visibleRows.length === 0 ? (
                    <tr><td colSpan={15} className="sw-empty"><p>Nessuna riga con movimenti per questo giorno</p></td></tr>
                  ) : visibleRows.map((r) => {
                    const isSubtotale = (r.tipo || "").toUpperCase() === "T";
                    const subStyle = isSubtotale
                      ? { background: "#d0e7ff", fontWeight: 700, borderTop: "2px solid #1e88e5", borderBottom: "2px solid #1e88e5" }
                      : undefined;
                    const rowStyle = r.non_stampa && !isSubtotale
                      ? { opacity: 0.55, fontStyle: "italic" }
                      : subStyle;
                    const rowClass = r.non_stampa && !isSubtotale ? "hide-in-print" : undefined;
                    return (
                      <tr key={r.conto} data-testid={`elab-row-${r.conto}`} className={rowClass} style={rowStyle}>
                        <td className="center-col" style={{ fontWeight: 700, fontFamily: "Consolas" }}>{r.conto}</td>
                        <td style={{ fontWeight: 600 }}>{isSubtotale ? `⮕ SUBTOTALE · ${r.descrizione}` : r.descrizione}</td>
                        <td>{r.modo_label}</td>
                        <td className="center-col">{r.competenza}{r.target_anno ? ` (${r.target_anno})` : ""}</td>
                        <td>{r.plesso_label}</td>
                        <td>{r.modalita_label}</td>
                        <td>{r.causale1_label}</td>
                        <td>{r.causale2_label}</td>
                        <td>{r.causale3_label}</td>
                        <td className="center-col" style={{ fontFamily: "Consolas" }}>{r.cella}</td>
                        <td className="center-col">{r.tipo}</td>
                        <td className="center-col">{r.non_stampa ? "✓" : ""}</td>
                        <td className="num-col" style={{ background: isSubtotale ? "#b3d4fc" : "#fffde7" }}>{r.num_movimenti.toLocaleString("it-IT")}</td>
                        <td className="num-col" style={{ background: isSubtotale ? "#80b6f0" : "#fff59d", fontWeight: 700, fontSize: 13, color: r.totale < 0 ? "#cc0000" : r.totale > 0 ? "#1b5e20" : "#888" }}>€ {fmtEuro(r.totale)}</td>
                        <td className="center-col no-print">
                          {!isSubtotale && (
                            <button
                              data-testid={`btn-dettaglio-${r.conto}`}
                              onClick={() => apriDettaglio(r.conto)}
                              title="Vedi movimenti del giorno"
                              style={{ background: "transparent", border: "1px solid #0066cc", color: "#0066cc", padding: "1px 6px", fontSize: 11, cursor: "pointer", borderRadius: 2 }}
                            >👁</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {snapshot && visibleRows.length > 0 && (
                  <tfoot>
                    <tr style={{ fontWeight: 700, background: "#e0e0e0" }}>
                      <td colSpan={11}>TOTALE GENERALE (solo righe V)</td>
                      <td></td>
                      <td className="num-col" style={{ background: "#fff59d" }}>{visibleRows.filter((r) => (r.tipo || "").toUpperCase() === "V").reduce((s, r) => s + r.num_movimenti, 0).toLocaleString("it-IT")}</td>
                      <td className="num-col" style={{ background: "#ffb300", fontSize: 14 }}>€ {fmtEuro(visibleRows.filter((r) => (r.tipo || "").toUpperCase() === "V").reduce((s, r) => s + r.totale, 0))}</td>
                      <td className="no-print"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>

        {/* Storico elaborazioni — nascosto in modalità periodo (richiesta utente) */}
        <div className="no-print" style={{ display: forcedMode === "periodo" ? "none" : undefined }}>
          {(() => {
            const tipoAttivo = forcedMode === "cassa" ? "cassa" : forcedMode === "periodo" ? "pdc" : storicoFilter;
            const visibili = forcedMode
              ? storico.filter(s => (s.tipo || "pdc") === tipoAttivo)
              : storico;
            const labelStorico = forcedMode === "cassa" ? "Storico Cassa" : forcedMode === "periodo" ? "Storico PDC" : "Storico elaborazioni";
            return (
          <div style={{ padding: "8px 12px", background: "#f5f5f5", border: "1px solid #ccc", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            <span>{labelStorico} ({visibili.length})</span>
            {visibili.length > 0 && (
              <button
                data-testid="btn-storico-delete-all"
                title={forcedMode ? `Elimina tutti gli snapshot ${tipoAttivo === "cassa" ? "Cassa" : "PDC"}` : "Elimina tutti gli snapshot"}
                onClick={async () => {
                  const txt = forcedMode
                    ? `Eliminare tutti gli snapshot ${tipoAttivo === "cassa" ? "Cassa" : "PDC"} (${visibili.length})?`
                    : `Eliminare TUTTI gli snapshot (${visibili.length})? L'operazione non è reversibile.`;
                  if (!window.confirm(txt)) return;
                  try {
                    // Un'unica chiamata server-side (veloce anche con centinaia di snapshot)
                    await deleteAllElaborazioni(forcedMode ? tipoAttivo : null);
                    setSnapshot(null);
                    setPeriodoResult(null);
                    setCassaResult(null);
                    await caricaStorico();
                  } catch (err) {
                    alert(err?.response?.data?.detail || "Errore eliminazione");
                  }
                }}
                style={{ marginLeft: "auto", background: "#cc0000", color: "white", border: "none", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
              >✕ Elimina tutti</button>
            )}
          </div>
            );
          })()}
          {/* Filtro tipo - nascosto quando c'è forcedMode */}
          {!forcedMode && (
          <div style={{ display: "flex", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc", background: "white" }}>
            {[
              { k: "all", label: "Tutti", color: "#555" },
              { k: "pdc", label: "PDC", color: "#1565c0" },
              { k: "cassa", label: "Cassa", color: "#2e7d32" },
            ].map(opt => {
              const active = storicoFilter === opt.k;
              const count = opt.k === "all" ? storico.length : storico.filter(s => (s.tipo || "pdc") === opt.k).length;
              return (
                <button
                  key={opt.k}
                  data-testid={`storico-filter-${opt.k}`}
                  onClick={() => setStoricoFilter(opt.k)}
                  style={{
                    flex: 1,
                    padding: "5px 6px",
                    border: "none",
                    borderBottom: active ? `3px solid ${opt.color}` : "3px solid transparent",
                    background: active ? "#f4f9ff" : "transparent",
                    color: active ? opt.color : "#666",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >{opt.label} <span style={{ fontWeight: 400, color: "#999" }}>({count})</span></button>
              );
            })}
          </div>
          )}
          <div style={{ border: "1px solid #ccc", borderTop: "none", maxHeight: "70vh", overflow: "auto" }}>
            {(() => {
              const giorniSett = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
              // Filter weekends, sort by calendar date descending
              const parse = (dmy) => {
                const [d, m, y] = (dmy || "").split("/");
                if (!d || !m || !y) return null;
                return new Date(+y, +m - 1, +d);
              };
              const filtered = storico
                .filter(it => storicoFilter === "all" || (it.tipo || "pdc") === storicoFilter)
                .map(it => ({ ...it, _dt: parse(it.data) }))
                .filter(it => it._dt && it._dt.getDay() !== 0 && it._dt.getDay() !== 6)
                .sort((a, b) => a._dt - b._dt);
              // Find oldest updated_at among same (data, tipo)
              const oldestByKey = {};
              filtered.forEach(it => {
                const key = `${it.data}|${it.tipo || "pdc"}`;
                if (!oldestByKey[key] || (it.updated_at && it.updated_at < oldestByKey[key])) {
                  oldestByKey[key] = it.updated_at;
                }
              });
              const countByKey = filtered.reduce((acc, it) => {
                const k = `${it.data}|${it.tipo || "pdc"}`;
                acc[k] = (acc[k] || 0) + 1;
                return acc;
              }, {});
              if (filtered.length === 0) {
                return <div style={{ padding: 20, fontSize: 11, color: "#888", textAlign: "center" }}>Nessuna elaborazione feriale salvata</div>;
              }
              return filtered.map((item) => {
                const tipo = item.tipo || "pdc";
                const isActive = (
                  (tipo === "pdc" && snapshot && snapshot.data === item.data && snapshot.anno === item.anno) ||
                  (tipo === "cassa" && cassaResult && cassaResult.data_da === item.data && cassaResult.data_a === item.data)
                );
                const dayName = item._dt ? giorniSett[item._dt.getDay()] : "";
                const key = `${item.data}|${tipo}`;
                const hasMultiple = countByKey[key] > 1;
                const isOldestOfDay = hasMultiple && item.updated_at === oldestByKey[key];
                const isCassa = tipo === "cassa";
                return (
                  <div
                    key={`${tipo}-${item.data}-${item.anno || "na"}`}
                    data-testid={`storico-${tipo}-${item.data}`}
                    style={{
                      padding: "8px 12px",
                      borderBottom: "1px solid #eee",
                      background: isActive ? "#e3f2fd" : (isOldestOfDay ? "#fff3cd" : "white"),
                      borderLeft: `4px solid ${isOldestOfDay ? "#f9a825" : (isCassa ? "#2e7d32" : "#1565c0")}`,
                      fontSize: 11,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div
                      onClick={() => caricaStoricoItem(item)}
                      style={{ flex: 1, cursor: "pointer" }}
                    >
                      <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ background: isCassa ? "#2e7d32" : "#1565c0", color: "white", fontSize: 9, padding: "1px 6px", borderRadius: 2, fontWeight: 700, textTransform: "uppercase" }}>
                          {isCassa ? "CASSA" : "PDC"}
                        </span>
                        <span>{dayName} {item.data}</span>
                        {!isCassa && item.anno && <span style={{ color: "#666", fontWeight: 400 }}>· Anno {item.anno}</span>}
                        {isOldestOfDay && <span title="Elaborazione più vecchia per questa data" style={{ background: "#f9a825", color: "white", fontSize: 9, padding: "1px 5px", borderRadius: 2, fontWeight: 700 }}>OBSOLETA</span>}
                      </div>
                      <div style={{ color: "#0a7d0a", fontWeight: 600, fontSize: 12 }}>€ {fmtEuro(item.grand_total)}</div>
                      <div style={{ color: "#888" }}>{fmtUpdated(item.updated_at)}</div>
                    </div>
                    <button
                      data-testid={`btn-storico-delete-${tipo}-${item.data}`}
                      title="Elimina questa elaborazione"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Eliminare l'elaborazione ${tipo.toUpperCase()} del ${item.data}${item.anno ? ' anno ' + item.anno : ''}?`)) return;
                        try {
                          await deleteElaborazioneGiorno(item.data, item.anno, tipo);
                          if (isActive) {
                            if (isCassa) setCassaResult(null);
                            else setSnapshot(null);
                          }
                          await caricaStorico();
                        } catch (err) {
                          alert(err?.response?.data?.detail || "Errore eliminazione");
                        }
                      }}
                      style={{
                        background: "transparent",
                        border: "1px solid #cc0000",
                        color: "#cc0000",
                        padding: "2px 8px",
                        fontSize: 11,
                        cursor: "pointer",
                        borderRadius: 2,
                        fontWeight: 700,
                      }}
                    >✕</button>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {detail && (
        <DettaglioModal
          detail={detail}
          loading={detailLoading}
          giorno={snapshot?.data}
          anno={snapshot?.anno}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function DettaglioModal({ detail, loading, giorno, anno, onClose }) {
  const [soloMatched, setSoloMatched] = useState(true);
  const [filtroAlunno, setFiltroAlunno] = useState("");
  if (!detail) return null;
  const movimenti = detail.movimenti || [];
  const fq = filtroAlunno.trim().toLowerCase();
  let visible = soloMatched ? movimenti.filter(m => m.matched) : movimenti;
  if (fq) {
    visible = visible.filter(m =>
      (m.alu_nome || "").toLowerCase().includes(fq) ||
      String(m.Alu || "").toLowerCase().includes(fq) ||
      (m.CauDes || "").toLowerCase().includes(fq) ||
      (m.ModDes || "").toLowerCase().includes(fq)
    );
  }

  return (
    <div
      data-testid="elab-detail-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}
    >
      <div style={{ background: "white", width: "94%", maxWidth: 1400, maxHeight: "92vh", display: "flex", flexDirection: "column", border: "1px solid #999", borderRadius: 4 }} onClick={e => e.stopPropagation()}>
        <div style={{ background: "#0066cc", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            Conto <span style={{ fontFamily: "Consolas" }}>{detail.conto}</span> · {detail.descrizione || ""}
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85, marginLeft: 10 }}>
              Giorno {giorno} · Anno {anno}
            </span>
          </div>
          <button onClick={onClose} data-testid="btn-close-detail" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 20, fontWeight: 700 }}>✕</button>
        </div>

        <div style={{ padding: "10px 16px", borderBottom: "1px solid #ddd", background: "#fafafa", fontSize: 12, display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
          <div><b>Tipo:</b> {detail.tipo || "-"}</div>
          <div><b>Competenza:</b> {detail.competenza || "-"}{detail.target_anno ? ` (${detail.target_anno})` : ""}</div>
          <div><b>Modalità OK:</b> {(detail.modalita_codici || []).join(", ") || "—"}</div>
          <div><b>Causali OK:</b> {(detail.causali_codici || []).join(", ") || "—"}</div>
          <div><b>Settori OK:</b> {(detail.settori || []).join(", ") || "—"}</div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 16, alignItems: "center" }}>
            <div style={{ background: "#e8f5e9", border: "1px solid #81c784", padding: "3px 10px", borderRadius: 3, fontWeight: 600 }}>
              Totalizzati: <b>{detail.matched_count || 0}</b> · € {fmtEuro(detail.matched_total || 0)}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
              <input type="checkbox" checked={soloMatched} onChange={e => setSoloMatched(e.target.checked)} />
              Solo totalizzati
            </label>
            <input type="text" value={filtroAlunno} onChange={e => setFiltroAlunno(e.target.value)}
              data-testid="elab-mov-search-alunno"
              placeholder="🔍 Filtra alunno..."
              style={{ padding: "4px 8px", border: "1px solid #1976d2", borderRadius: 3, fontSize: 11, minWidth: 220, fontWeight: 600 }} />
          </div>
        </div>

        <div style={{ overflow: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", fontSize: 12 }}>Caricamento...</div>
          ) : visible.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", fontSize: 12, color: "#888" }}>Nessun movimento per questo giorno</div>
          ) : (
            <table className="sw-table" style={{ fontSize: 11 }}>
              <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 1 }}>
                <tr>
                  <th style={{ width: 30, textAlign: "center" }}></th>
                  <th style={{ width: 60 }}>Num</th>
                  <th style={{ width: 60 }}>Cod</th>
                  <th>Alunno</th>
                  <th style={{ width: 60, textAlign: "center" }}>Sett.</th>
                  <th style={{ width: 70, textAlign: "center" }}>Cod Mod</th>
                  <th style={{ width: 110 }}>Modalità</th>
                  <th style={{ width: 70, textAlign: "center" }}>Cod Cau</th>
                  <th style={{ width: 170 }}>Causale</th>
                  <th style={{ width: 70, textAlign: "center" }}>AnnCom</th>
                  <th style={{ width: 90, textAlign: "center" }}>Scadenza</th>
                  <th style={{ width: 90, textAlign: "center" }}>Pagamento</th>
                  <th style={{ width: 110, textAlign: "right" }}>Importo €</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m, i) => (
                  <tr key={`${m.Num}-${i}`} style={m.matched ? { background: "#fff59d", fontWeight: 600 } : undefined}>
                    <td className="center-col">{m.matched ? <span style={{ color: "#1b5e20", fontWeight: 700 }}>✓</span> : ""}</td>
                    <td className="center-col">{m.Num}</td>
                    <td className="center-col">{m.Alu}</td>
                    <td>{m.alu_nome}</td>
                    <td className="center-col">{m.settore || "-"}</td>
                    <td className="center-col" style={{ fontFamily: "Consolas" }}>{m.ModCod || m.Mod}</td>
                    <td>{m.ModDes}</td>
                    <td className="center-col" style={{ fontFamily: "Consolas" }}>{m.CauCod || m.Cau}</td>
                    <td>{m.CauDes}</td>
                    <td className="center-col">{m.AnnCom}</td>
                    <td className="center-col">{m.DtaSca}</td>
                    <td className="center-col">{m.DtaPag}</td>
                    <td className="num-col" style={{ color: m.Imp < 0 ? "#cc0000" : "#1b5e20", fontWeight: 700 }}>€ {fmtEuro(m.Imp)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#e0e0e0", fontWeight: 700 }}>
                  <td colSpan={12} style={{ textAlign: "right" }}>TOTALE {soloMatched ? "(totalizzati)" : "(visibile)"}:</td>
                  <td className="num-col">€ {fmtEuro(visible.reduce((s, m) => s + (m.Imp || 0), 0))}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


function PeriodoSummary({ result, onOpenDay, onBack }) {
  const [view, setView] = useState("riassunto"); // "riassunto" | "pivot"
  const [pivot, setPivot] = useState(null);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [onlyMovimentati, setOnlyMovimentati] = useState(true);
  const [nascondiNonStampa, setNascondiNonStampa] = useState(false);

  useEffect(() => {
    if (view !== "pivot") return;
    setPivotLoading(true);
    getPdcPivot(result.data_da, result.data_a, result.anno, onlyMovimentati)
      .then(r => setPivot(r.data))
      .catch(e => alert(e?.response?.data?.detail || "Errore pivot"))
      .finally(() => setPivotLoading(false));
  }, [view, onlyMovimentati, result.data_da, result.data_a, result.anno]);

  const giorni = result?.giorni || [];
  const giorniConMov = giorni.filter(g => (g.total_movimenti || 0) > 0);
  return (
    <div data-testid="periodo-summary">
      <div style={{ padding: "10px 14px", background: "#e3f2fd", border: "1px solid #64b5f6", borderRadius: 3, marginBottom: 10, fontSize: 12, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
        {onBack && (
          <button data-testid="periodo-back-list" onClick={onBack}
            style={{ background: "#0288d1", color: "white", border: "none", padding: "5px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
            ← Lista periodi
          </button>
        )}
        <div>Periodo: <b>dal {result.data_da} al {result.data_a}</b></div>
        <div>Anno: <b>{result.anno}</b></div>
        <div>Giorni elaborati: <b>{result.giorni_processati}</b></div>
        <div>Giorni con movimenti: <b>{giorniConMov.length}</b></div>
        <div>Totale movimenti del periodo: <b>{(result.totale_movimenti_periodo || 0).toLocaleString("it-IT")}</b></div>
        <div style={{ marginLeft: "auto", fontWeight: 700, fontSize: 14 }}>
          TOTALE PERIODO: <span style={{ color: "#1b5e20" }}>€ {fmtEuro(result.grand_total_periodo || 0)}</span>
        </div>
      </div>

      <div className="no-print" style={{ display: "flex", background: "#f0f0f0", border: "1px solid #ccc", borderRadius: 3, overflow: "hidden", width: "fit-content", marginBottom: 10, alignItems: "center" }}>
        <button
          data-testid="periodo-view-riassunto"
          onClick={() => setView("riassunto")}
          style={{ padding: "5px 14px", border: "none", background: view === "riassunto" ? "#0066cc" : "transparent", color: view === "riassunto" ? "white" : "#333", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
        >Riassunto per giorno</button>
        <button
          data-testid="periodo-view-pivot"
          onClick={() => setView("pivot")}
          style={{ padding: "5px 14px", border: "none", background: view === "pivot" ? "#0066cc" : "transparent", color: view === "pivot" ? "white" : "#333", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
        >Tabellare PDC (pivot)</button>
        {view === "pivot" && (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, padding: "0 10px", borderLeft: "1px solid #ccc", cursor: "pointer" }}>
              <input type="checkbox" checked={onlyMovimentati} onChange={e => setOnlyMovimentati(e.target.checked)} />
              Solo conti movimentati
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, padding: "0 10px", borderLeft: "1px solid #ccc", cursor: "pointer" }}>
              <input type="checkbox" checked={nascondiNonStampa} onChange={e => setNascondiNonStampa(e.target.checked)} />
              Nascondi righe "Non Stampa"
            </label>
            <button
              data-testid="btn-export-pivot-excel"
              onClick={() => {
                const API = process.env.REACT_APP_BACKEND_URL;
                const params = new URLSearchParams({
                  data_da: result.data_da,
                  data_a: result.data_a,
                  anno: String(result.anno),
                  only_movimentati: String(!!onlyMovimentati),
                  nascondi_non_stampa: String(!!nascondiNonStampa),
                });
                window.open(`${API}/api/elaborazione/pdc-pivot/export?${params.toString()}`, "_blank");
              }}
              title="Esporta la tabella in formato Excel (.xlsx)"
              style={{ background: "#1e7e34", color: "white", border: "none", padding: "6px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderLeft: "1px solid #ccc" }}
            >📊 Esporta Excel</button>
          </>
        )}
      </div>

      {view === "riassunto" ? (
        <div className="sw-table-container">
        <div style={{ maxHeight: "70vh", overflow: "auto" }}>
          <table className="sw-table" style={{ fontSize: 11 }}>
            <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 1 }}>
              <tr>
                <th style={{ width: 30, textAlign: "center" }}>#</th>
                <th style={{ width: 120 }}>Giorno</th>
                <th style={{ width: 120 }}>Giorno sett.</th>
                <th style={{ width: 130, textAlign: "right" }}>Numero movimenti del giorno</th>
                <th style={{ width: 130, textAlign: "right" }}>Righe con valori dello schema</th>
                <th style={{ width: 180, textAlign: "right", background: "#ffd54f", color: "#000" }}>TOTALE €</th>
                <th className="no-print" style={{ width: 80, textAlign: "center" }}></th>
              </tr>
            </thead>
            <tbody>
              {giorni.map((g, i) => {
                const vuoto = !g.total_movimenti;
                const [dd, mm, yyyy] = g.data.split("/");
                const dt = new Date(+yyyy, +mm - 1, +dd);
                const dow = dt.toLocaleDateString("it-IT", { weekday: "long" });
                const weekend = dt.getDay() === 0 || dt.getDay() === 6; // Dom / Sab
                const rowStyle = weekend
                  ? { color: "#888", background: "#f0f2f5", fontStyle: "italic" }
                  : vuoto
                    ? { color: "#aaa", background: "#fafafa" }
                    : undefined;
                return (
                  <tr key={g.data} data-testid={`periodo-row-${g.data}`} style={rowStyle}>
                    <td className="center-col">{i + 1}</td>
                    <td style={{ fontWeight: 600, fontFamily: "Consolas" }}>{g.data}</td>
                    <td style={{ textTransform: "capitalize", fontWeight: weekend ? 600 : 400 }}>{dow}</td>
                    <td className="num-col">{(g.total_movimenti || 0).toLocaleString("it-IT")}</td>
                    <td className="num-col">{(g.righe_movimentate || 0).toLocaleString("it-IT")}</td>
                    <td className="num-col" style={{ background: weekend ? "#e8eaed" : (vuoto ? "#f5f5f5" : "#fff59d"), fontWeight: 700, fontSize: 13, color: weekend ? "#888" : (vuoto ? "#aaa" : (g.grand_total < 0 ? "#cc0000" : "#1b5e20")) }}>€ {fmtEuro(g.grand_total)}</td>
                    <td className="center-col no-print">
                      <button
                        data-testid={`btn-apri-giorno-${g.data}`}
                        onClick={() => onOpenDay(g.data)}
                        style={{ background: "#0066cc", border: "1px solid #0066cc", color: "white", padding: "3px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                        title="Vedi sviluppo del giorno"
                      >Vedi sviluppo</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: "#e0e0e0" }}>
                <td colSpan={3}>TOTALE PERIODO</td>
                <td className="num-col">{(result.totale_movimenti_periodo || 0).toLocaleString("it-IT")}</td>
                <td></td>
                <td className="num-col" style={{ background: "#ffb300", fontSize: 14 }}>€ {fmtEuro(result.grand_total_periodo || 0)}</td>
                <td className="no-print"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      ) : (
        <PdcPivotTable
          pivot={pivot}
          loading={pivotLoading}
          nascondiNonStampa={nascondiNonStampa}
        />
      )}
    </div>
  );
}

function PdcPivotTable({ pivot, loading, nascondiNonStampa }) {
  if (loading) return <div style={{ padding: 40, textAlign: "center", fontSize: 12 }}>Caricamento pivot...</div>;
  if (!pivot) return null;
  const accounts = (pivot.accounts || []).filter(a => !nascondiNonStampa || !a.non_stampa);
  const days = (pivot.days || []).filter(d => !d.is_weekend);

  return (
    <div data-testid="pdc-pivot">
      <div style={{ fontSize: 11, color: "#666", marginBottom: 6 }}>
        {pivot.days_with_snapshot} / {pivot.days_total} giorni con snapshot disponibile ·
        {" "}{accounts.length} voci PDC visualizzate
      </div>
      <div className="sw-table-container">
        <div style={{ maxHeight: "75vh", overflow: "auto" }}>
          <table className="sw-table sw-table-compact" style={{ fontSize: 10 }}>
            <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 2 }}>
              <tr>
                <th style={{ width: 72, position: "sticky", left: 0, background: "#e8e8e8", zIndex: 3 }}>Giorno</th>
                <th style={{ width: 52, position: "sticky", left: 72, background: "#e8e8e8", zIndex: 3 }}>Sett.</th>
                {accounts.map(a => {
                  const isT = (a.tipo || "").toUpperCase() === "T";
                  return (
                    <th
                      key={a.conto}
                      title={a.descrizione}
                      style={{
                        minWidth: 54,
                        maxWidth: 90,
                        textAlign: "right",
                        background: isT ? "#d0e7ff" : (a.non_stampa ? "#eee" : "#e8e8e8"),
                        fontSize: 9,
                        verticalAlign: "bottom",
                        padding: "3px 4px",
                      }}
                    >
                      <div style={{ fontFamily: "Consolas", fontWeight: 700 }}>{a.conto}</div>
                      <div style={{ fontSize: 8, fontWeight: 400, color: "#333", maxWidth: 85, whiteSpace: "normal", lineHeight: 1.15, minHeight: 22 }}>
                        {(a.descrizione || "").slice(0, 24)}
                      </div>
                    </th>
                  );
                })}
                <th style={{ minWidth: 80, textAlign: "right", background: "#ffd54f", color: "#000", position: "sticky", right: 0, zIndex: 3, fontSize: 10 }}>TOTALE €</th>
              </tr>
            </thead>
            <tbody>
              {days.map(d => (
                <tr key={d.data} style={!d.has_snapshot ? { opacity: 0.5, fontStyle: "italic" } : undefined}>
                  <td style={{ fontFamily: "Consolas", fontWeight: 700, position: "sticky", left: 0, background: "white", borderRight: "1px solid #ddd", fontSize: 10 }}>{d.data}</td>
                  <td style={{ textTransform: "capitalize", position: "sticky", left: 72, background: "white", borderRight: "1px solid #ddd", fontSize: 10 }}>{(d.giorno_sett || "").slice(0, 3)}{!d.has_snapshot && " (n/d)"}</td>
                  {accounts.map(a => {
                    const c = d.cells?.[a.conto];
                    const v = c?.totale || 0;
                    const isT = (a.tipo || "").toUpperCase() === "T";
                    return (
                      <td
                        key={a.conto}
                        className="num-col"
                        style={{
                          background: isT ? "#d0e7ff" : (v !== 0 ? "#fff59d" : undefined),
                          color: v < 0 ? "#cc0000" : (v > 0 ? "#1b5e20" : "#ccc"),
                          fontWeight: v !== 0 ? 600 : 400,
                          fontSize: 10,
                        }}
                      >
                        {v === 0 ? "—" : fmtEuro(v)}
                      </td>
                    );
                  })}
                  <td className="num-col" style={{ background: d.row_total !== 0 ? "#fff59d" : "#f5f5f5", fontWeight: 700, color: d.row_total < 0 ? "#cc0000" : d.row_total > 0 ? "#1b5e20" : "#888", position: "sticky", right: 0, fontSize: 10 }}>
                    {fmtEuro(d.row_total || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: "#e0e0e0" }}>
                <td colSpan={2} style={{ position: "sticky", left: 0, background: "#e0e0e0", fontSize: 10 }}>TOT. PER VOCE</td>
                {accounts.map(a => {
                  const t = pivot.col_totals?.[a.conto] || { totale: 0, num: 0 };
                  const isT = (a.tipo || "").toUpperCase() === "T";
                  return (
                    <td key={a.conto} className="num-col" style={{ background: isT ? "#b3d4fc" : "#fff59d", fontSize: 10 }}>
                      {isT ? "—" : fmtEuro(t.totale || 0)}
                    </td>
                  );
                })}
                <td className="num-col" style={{ background: "#ffb300", fontSize: 12, position: "sticky", right: 0 }}>€ {fmtEuro(pivot.grand_total || 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}



function CassaTable({ result, overrides, onChangeSrc, onReset, onReElabora }) {
  const [editing, setEditing] = useState(null); // rowDate being edited
  const [tempValue, setTempValue] = useState("");
  const modalita = result?.modalita || [];
  const rows = result?.rows || [];
  const totals = result?.totals_per_modo || {};
  const bonifCount = modalita.filter(m => m.is_bonifico).length;

  const apriEdit = (r) => {
    setEditing(r.data);
    setTempValue(dmyToIso(r.bonifico_source || r.bonifico_source_computed));
  };
  const salvaEdit = async (r) => {
    const newDmy = isoToDmy(tempValue);
    if (newDmy && newDmy !== r.bonifico_source_computed) {
      onChangeSrc(r.data, newDmy);
    } else {
      onReset(r.data);
    }
    setEditing(null);
    setTimeout(() => onReElabora(), 100);
  };

  return (
    <div data-testid="cassa-result">
      <div className="no-print" style={{ padding: "10px 14px", background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 3, marginBottom: 10, fontSize: 12, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div>Elaborazione Cassa: <b>dal {result.data_da} al {result.data_a}</b></div>
        <div>Giorni: <b>{rows.length}</b> · Movimenti totali: <b>{(result.total_count || 0).toLocaleString("it-IT")}</b></div>
        <div style={{ marginLeft: "auto", fontWeight: 700, fontSize: 14 }}>
          TOTALE: <span style={{ color: "#1b5e20" }}>€ {fmtEuro(result.grand_total || 0)}</span>
        </div>
      </div>

      {bonifCount > 0 && (
        <div className="no-print" style={{ padding: "6px 12px", background: "#fff8e1", border: "1px solid #ffd54f", borderRadius: 3, marginBottom: 10, fontSize: 11 }}>
          <b>ℹ Nota Bonifico:</b> le colonne <i>bonifico</i> mostrano i movimenti del <b>giorno lavorativo precedente</b> (lun ⇐ ven, mar ⇐ lun, mer ⇐ mar, ecc.). <b>Sabato e domenica</b> non sommano nulla.
          Usa il pulsante ✎ nella colonna <i>Data Bonifico</i> per modificare manualmente la data in caso di festività infrasettimanali.
        </div>
      )}

      <div className="sw-table-container">
        <div style={{ maxHeight: "70vh", overflow: "auto" }}>
          <table className="sw-table sw-table-compact" style={{ fontSize: 10 }}>
            <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 1 }}>
              <tr>
                <th style={{ width: 72 }}>Giorno</th>
                <th style={{ width: 52 }}>Sett.</th>
                {bonifCount > 0 && <th style={{ width: 110 }}>Data Bonif.</th>}
                {modalita.map(m => (
                  <th key={m.codice} style={{ textAlign: "right", minWidth: 78, maxWidth: 120, background: m.is_bonifico ? "#fce4ec" : undefined, fontSize: 10 }}>
                    {m.descrizione}
                    {m.is_bonifico && <div style={{ fontSize: 9, color: "#d81b60", fontWeight: 400 }}>(giorno prec.)</div>}
                  </th>
                ))}
                <th style={{ textAlign: "right", minWidth: 88, background: "#ffd54f", color: "#000", fontSize: 10 }}>TOTALE €</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.data} data-testid={`cassa-row-${r.data}`} style={r.is_weekend ? { background: "#f5f5f5", color: "#888" } : undefined}>
                  <td style={{ fontWeight: 600, fontFamily: "Consolas", fontSize: 10 }}>{r.data}</td>
                  <td style={{ textTransform: "capitalize", fontSize: 10 }}>{(r.giorno_sett || "").slice(0, 3)}</td>
                  {bonifCount > 0 && (
                    <td style={{ fontSize: 10, background: r.bonifico_override_active ? "#fff176" : undefined }}>
                      {editing === r.data ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <input type="date" value={tempValue} onChange={e => setTempValue(e.target.value)} style={{ fontSize: 10, padding: 1, width: 112 }} />
                          <button onClick={() => salvaEdit(r)} style={{ background: "#2e7d32", color: "white", border: "none", padding: "1px 5px", fontSize: 10, cursor: "pointer" }} title="Conferma">✓</button>
                          <button onClick={() => setEditing(null)} style={{ background: "#888", color: "white", border: "none", padding: "1px 5px", fontSize: 10, cursor: "pointer" }} title="Annulla">✕</button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <span style={{ fontFamily: "Consolas", fontWeight: r.bonifico_override_active ? 700 : 400, color: r.bonifico_source ? undefined : "#aaa" }}>{r.bonifico_source || "—"}</span>
                          <button className="no-print" onClick={() => apriEdit(r)} data-testid={`btn-edit-bonifico-${r.data}`} style={{ border: "1px solid #0066cc", background: "white", color: "#0066cc", padding: "0 3px", fontSize: 10, cursor: "pointer", borderRadius: 2 }} title="Modifica data bonifico">✎</button>
                          {r.bonifico_override_active && <button className="no-print" onClick={() => { onReset(r.data); setTimeout(() => onReElabora(), 100); }} style={{ border: "1px solid #cc0000", background: "white", color: "#cc0000", padding: "0 3px", fontSize: 10, cursor: "pointer", borderRadius: 2 }} title="Torna al calcolo automatico">↺</button>}
                        </div>
                      )}
                    </td>
                  )}
                  {modalita.map(m => {
                    const c = r.cells?.[String(m.codice)] || { totale: 0, num: 0 };
                    const v = c.totale || 0;
                    return (
                      <td key={m.codice} className="num-col" style={{ background: v !== 0 ? (m.is_bonifico ? "#fce4ec" : "#fffde7") : undefined, color: v < 0 ? "#cc0000" : undefined, fontSize: 10 }}>
                        {v === 0 ? <span style={{ color: "#ccc" }}>—</span> : fmtEuro(v)}
                        {c.num > 0 && <div style={{ fontSize: 8, color: "#888", fontWeight: 400 }}>({c.num})</div>}
                      </td>
                    );
                  })}
                  <td className="num-col" style={{ fontWeight: 700, background: r.row_total !== 0 ? "#fff59d" : "#f5f5f5", color: r.row_total < 0 ? "#cc0000" : r.row_total > 0 ? "#1b5e20" : "#888", fontSize: 11 }}>
                    € {fmtEuro(r.row_total || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: "#e0e0e0" }}>
                <td colSpan={bonifCount > 0 ? 3 : 2} style={{ fontSize: 10 }}>TOT. PER MODALITÀ</td>
                {modalita.map(m => {
                  const t = totals[String(m.codice)] || { totale: 0, num: 0 };
                  return (
                    <td key={m.codice} className="num-col" style={{ background: "#fff59d", fontSize: 10 }}>
                      € {fmtEuro(t.totale || 0)}
                      {t.num > 0 && <div style={{ fontSize: 8, color: "#666", fontWeight: 400 }}>({t.num} mov)</div>}
                    </td>
                  );
                })}
                <td className="num-col" style={{ background: "#ffb300", fontSize: 12 }}>€ {fmtEuro(result.grand_total || 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// PeriodiList — Vista 1 di Elaborazione Periodo: elenco dei periodi
// (mesi) per cui esistono snapshot PDC salvati.
// =====================================================================
function PeriodiList({ loading, items, onOpen, onRefresh }) {
  const fmtUpd = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <div data-testid="periodi-list">
      <div style={{ padding: "12px 18px", background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 4, marginBottom: 14, fontSize: 12, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>📅 Periodi elaborati</strong>
        <span style={{ color: "#555" }}>{items.length} periodi nel sistema · clicca su una card per aprire lo sviluppo</span>
        <button onClick={onRefresh} data-testid="periodi-refresh"
          style={{ marginLeft: "auto", background: "transparent", border: "1px solid #2e7d32", color: "#2e7d32", padding: "4px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          ↻ Aggiorna
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Caricamento...</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#888", border: "1px dashed #ccc", borderRadius: 4 }}>
          <p style={{ margin: 0 }}>Nessun periodo elaborato. Usa la toolbar in alto per impostare un range di date e clicca <b>Elabora periodo</b>.</p>
        </div>
      ) : (
        <div data-testid="periodi-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14, padding: "0 4px 14px 4px" }}>
          {items.map(p => {
            const totColor = p.grand_total < 0 ? "#c62828" : "#1b5e20";
            return (
              <button
                key={p.key}
                data-testid={`btn-apri-periodo-${p.key}`}
                onClick={() => onOpen(p)}
                title={`Apri ${p.mese_label}`}
                style={{
                  textAlign: "left",
                  background: "white",
                  border: "1px solid #bbdefb",
                  borderRadius: 6,
                  padding: 14,
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  transition: "transform 0.08s, box-shadow 0.08s, border-color 0.08s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(13,71,161,0.14)"; e.currentTarget.style.borderColor = "#1976d2"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; e.currentTarget.style.borderColor = "#bbdefb"; }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#0d47a1", textTransform: "capitalize", flex: 1 }}>{p.mese_label}</div>
                  <span style={{ fontSize: 10, color: "#1976d2", background: "#e3f2fd", padding: "2px 6px", borderRadius: 10, fontWeight: 700 }}>AS {p.anno}/{String(p.anno + 1).slice(-2)}</span>
                </div>
                <div style={{ fontSize: 11, color: "#666", fontFamily: "Consolas, monospace" }}>{p.data_da} → {p.data_a}</div>
                <div style={{ height: 1, background: "#eceff1", margin: "4px 0" }}></div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.4 }}>Totale</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: totColor, fontFamily: "Consolas, monospace" }}>€ {fmtEuro(p.grand_total)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#555" }}>
                  <span><b>{p.giorni_elaborati}</b> giorni</span>
                  <span><b>{p.totale_movimenti}</b> movimenti</span>
                </div>
                {fmtUpd(p.updated_at) && (
                  <div style={{ fontSize: 9.5, color: "#999", marginTop: 4, borderTop: "1px dashed #eceff1", paddingTop: 4 }}>
                    Aggiornato il {fmtUpd(p.updated_at)}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

